/**
 * Whether the microphone is HEARING anything — the half of "is the mic
 * working" that a live track cannot answer.
 *
 * A granted, live, unmuted `MediaStreamTrack` is the only signal the app had,
 * and it is not enough: Windows can hand the browser a track from an input
 * that is muted at the device, and every indicator in the app says the room is
 * being recorded while the encoder writes an hour of digital silence. That is
 * not a hypothetical — a whole exam was recorded that way before anyone found
 * out, weeks later, by trying to listen to it.
 *
 * The maths is deliberately dumb and lives here, away from the AudioContext,
 * so the thresholds can be argued about in a test rather than in a browser.
 */

/**
 * RMS of one analyser frame, 0-1.
 *
 * `getByteTimeDomainData` centres silence on 128, so each sample is read as a
 * deviation from that rather than an absolute level.
 */
export function rmsOf(samples: Uint8Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (const sample of samples) {
    const deviation = (sample - 128) / 128;
    sum += deviation * deviation;
  }
  return Math.sqrt(sum / samples.length);
}

/**
 * Above this, the METER says something was heard.
 *
 * This is a speech-ish level on purpose: the bar exists so a student can prove
 * to themselves that talking moves it. It is NOT what the exam reports on —
 * see `DEAD_RMS`, and the note there about why the two must differ.
 */
export const HEARD_RMS = 0.012;

/** Whether this frame counts as having heard something, for the meter. */
export function heard(level: number): boolean {
  return level >= HEARD_RMS;
}

/**
 * At or below this, the input is DEAD rather than quiet.
 *
 * The distinction is the whole design. `getUserMedia({ audio: true })` gives
 * Chrome's default noise suppression and auto gain, which flatten a quiet room
 * to almost exactly the silence value — so "nobody spoke for a while" and "the
 * device is muted" look nearly identical, and reporting the first as the
 * second would put a warning on the majority of ordinary exams and make the
 * flag worth nothing.
 *
 * So the reported condition is the one a muted device actually produces:
 * essentially zero deviation, about one 8-bit step of dither, for far longer
 * than anyone sits silently. A student thinking quietly still registers well
 * above this; a muted input cannot rise above it.
 */
export const DEAD_RMS = 0.0025;

/** Whether this frame is the flat line a muted device produces. */
export function dead(level: number): boolean {
  return level <= DEAD_RMS;
}

/**
 * How long to listen before the SETUP row says "we cannot hear anything".
 *
 * Long enough that a student who simply has not spoken yet is not accused of a
 * broken microphone, short enough to be seen during setup rather than after
 * it.
 */
export const SILENCE_GRACE_MS = 6_000;

/**
 * How long a live microphone may read DEAD before the attempt reports it.
 *
 * Ten minutes, against a 6-second grace in setup, because the two prompts cost
 * different things. Setup is asking someone to speak; the exam is writing a
 * flag onto a student's attempt, and a flag that fires on a quiet paper is a
 * flag reviewers learn to ignore.
 */
export const SILENT_REPORT_MS = 600_000;

export interface SilenceReport {
  /** Since the last frame that was above `DEAD_RMS`. */
  deadForMs: number | null;
  /** False while the AudioContext is suspended — it reads silence either way. */
  listening: boolean;
  alreadyReported: boolean;
}

/**
 * Whether the exam should report this microphone as silent.
 *
 * Pulled out of the component because every clause here is a way to blame a
 * student's equipment for something else: a suspended audio context reads a
 * perfect mic as dead, and a report already sent must not be sent every frame
 * for the rest of the hour.
 */
export function shouldReportSilence({
  deadForMs,
  listening,
  alreadyReported,
}: SilenceReport): boolean {
  if (!listening || alreadyReported || deadForMs === null) return false;
  return deadForMs >= SILENT_REPORT_MS;
}

/**
 * The level meter's width, 0-1.
 *
 * Scaled against a loud-speech level rather than the full range: mapping RMS
 * straight to a bar leaves normal speech as a sliver, and a bar that barely
 * moves when you talk reads as a broken microphone, which is exactly the
 * wrong message.
 */
export function meterWidth(level: number): number {
  return Math.min(1, level / 0.25);
}
